import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { EntityImportRepository } from '../../Repositories';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BusyIndicatorMessageManager } from '../../../Busy-Indicator';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AgGridOptionsBuilder } from '../../AgGrid';
import * as _ from 'lodash';
import { IWeissmanModalComponent } from '../../WeissmanModalService';
import { MessageModalService } from '../../../UI-Lib/Message-Box/messageModal.service';

export interface ExcelPreviewComponentParams {
    importFileId: number;
}

@Component({
    selector: 'excel-preview',
    templateUrl: './excelPreview.component.html',
    styleUrls: ['./excelPreview.component.scss'],
    standalone: false,
    encapsulation: ViewEncapsulation.None
})
export class ExcelPreviewComponent implements OnInit, IWeissmanModalComponent<ExcelPreviewComponentParams, Compliance.ImportFileModel> {
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _entityImportRepository: EntityImportRepository,
        private readonly _messageModalService: MessageModalService) {
    }

    params: ExcelPreviewComponentParams;
    result: Compliance.ImportFileModel;

    selectedWorksheetName: string;
    headerRowIndex?: number = 1;
    previewInfo: Compliance.ExcelImportPreviewModel = null;
    busyMessageManager: BusyIndicatorMessageManager<string> = new BusyIndicatorMessageManager<string>();
    gridId: System.Guid = '11713691-1C0B-4EB5-A136-92EF182117A9';
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withContext(this)
        .withColumnResize()
        .withLoadingOverlay()
        .build();

    private _gridApi: GridApi;

    async ngOnInit(): Promise<void> {
        this.busyMessageManager.add('Loading', 'loading');
        try {
            await this._initialize();
        } catch(e) {
            if (e && e.status === 423) {
                try {
                    this.busyMessageManager.remove('loading');
                    await this._messageModalService.confirm(
                        `${e.error.message}  Are you sure you wish to take ownership?`,
                        'Confirm Take Ownership');
                } catch (e) {
                    this.cancel();
                    return;
                }

                this.busyMessageManager.remove('loading');

                await lastValueFrom(this._entityImportRepository.takeOwnership(this.params.importFileId));
                await this._initialize();
            }
        } finally {
            this.busyMessageManager.remove('loading');
        }
    }

    cancel(): void {
        this._bsModalRef.hide();
    }

    async save(): Promise<void> {
        this.busyMessageManager.add('Saving', 'saving');

        try {
            const model: Compliance.ImportFileExcelOptionsModel = {
                headerRowIndex: this.headerRowIndex,
                worksheetName: this.selectedWorksheetName
            };

            this.result = await lastValueFrom(this._entityImportRepository.updateExcelOptions(this.params.importFileId, model));

            this._bsModalRef.hide();
        } finally {
            this.busyMessageManager.remove('saving');
        }
    }

    selectedWorksheetChanged(): void {
        this._setRowData();
    }

    onAgGridReady(event: GridReadyEvent): void {
        this._gridApi = event.api;

        this._setRowData();
    }

    private _setRowData(): void {
        if (!this._gridApi || !this.previewInfo) {
            return;
        }

        const worksheetInfo = _.find(this.previewInfo.worksheetsInfo, i => i.worksheetName === this.selectedWorksheetName);
        const columns: ColDef[] = [
            {
                headerName: '',
                field: 'rowIndex',
                width: 100,
                minWidth: 50
            }
        ];

        for (let i = 0; i < worksheetInfo.columnsCount; i++) {
            columns.push({
                headerName: this._toColumnName(i + 1),
                field: `f${i}`,
                width: 100,
                minWidth: 50
            });
        }

        let rowIndex: number = 1;
        const data = _.map(worksheetInfo.rows, rawData => {
            const row = {
                ['rowIndex']: rowIndex++
            };
            for (let i = 0; i < worksheetInfo.columnsCount; i++) {
                row[`f${i}`] = rawData[i];
            }
            return row;
        });

        this._gridApi.setGridOption('columnDefs', columns);

        const allColumnIds = [];
        this._gridApi.getColumns().forEach((column) => {
            allColumnIds.push(column.getColId());
        });

        this._gridApi.setGridOption('rowData', data);
        this._gridApi.autoSizeColumns(allColumnIds);
    }

    private _toColumnName(num: number): string {
        let ret = '';
        for (let a = 1, b = 26; (num -= a) >= 0; a = b, b *= 26) {
            ret = String.fromCharCode((num % b) / a + 65) + ret;
        }
        return ret.toLocaleUpperCase();
    }

    private async _initialize(): Promise<void> {
        this.previewInfo = await lastValueFrom(this._entityImportRepository.getExcelPreviewAsync(this.params.importFileId));

        if (this.previewInfo.worksheetsInfo && this.previewInfo.worksheetsInfo.length > 0) {
            this.selectedWorksheetName = this.previewInfo.worksheetsInfo[0].worksheetName;
        }

        this._setRowData();
    }
}
